import asyncParallel from 'async/parallel';
import asyncSeries from 'async/series';
import React from 'react';
import EventEmitter from 'events';

import OrgStore from './org_store';
import PaymentMethodStore from './payment_method_store';
import UserStore from './user_store';
import VolumeStore from './volume_store';
import PlanStore from './plan_store';

import util from '../tools/util';

export default {
  init,
  useIsReady,
  useOrg,
  usePlan,
  useHasVolume,
  useHasPayment,
  createOrg,
  savePlan,
  updatePlan,
  finish,
};

const SIGNUP_ORG_KEY = 'ARES_SIGNUP_ORG_KEY';

let g_isReady = false;
let g_signupOrg: any = null;
let g_hasPayment = false;
let g_hasVolume = false;
let g_planId = '';

const g_eventEmitter = new EventEmitter();
const CHANGE_EVENT = 'change';
function _addChangeListener(callback) {
  g_eventEmitter.on(CHANGE_EVENT, callback);
}
function _removeChangeListener(callback) {
  g_eventEmitter.removeListener(CHANGE_EVENT, callback);
}
function _emit(...args) {
  g_eventEmitter.emit(CHANGE_EVENT, ...args);
}
function _subscribe(callback: any) {
  _addChangeListener(callback);
  return () => {
    _removeChangeListener(callback);
  };
}
function init(done?: (err?: any) => void) {
  function _complete() {
    UserStore.addChangeListener(_onUserChange);
    PaymentMethodStore.addChangeListener(_onPaymentChange);
    VolumeStore.addChangeListener(_onVolumeChange);
    PlanStore.addChangeListener(_onPlanChange);
    if (!g_signupOrg?.plan_id) {
      const plan_id = util.urlQueryGet('plan');
      if (plan_id) {
        g_planId = plan_id;
      }
    }
    g_isReady = true;
    _emit('change');
    done?.();
  }
  if (g_isReady) {
    done?.();
  } else {
    _loadOrg();
    if (!g_signupOrg) {
      _findPartialSignup();
    }
    if (g_signupOrg) {
      asyncParallel(
        [
          (done) =>
            VolumeStore.fetch(g_signupOrg.org_id, () => {
              _onVolumeChange();
              done();
            }),
          (done) =>
            PaymentMethodStore.fetch(g_signupOrg.org_id, () => {
              _onPaymentChange();
              done();
            }),
        ],
        _complete
      );
    } else {
      _complete();
    }
  }
}
function _findPartialSignup() {
  const org = UserStore.getOrg();
  if (org && org.is_admin && !org.plan_id) {
    g_signupOrg = org;
    _saveOrg(org.org_id);
  }
}
function _onUserChange() {
  const user = UserStore.getUser();
  if (!user && g_signupOrg) {
    g_signupOrg = null;
    g_hasPayment = false;
    g_hasVolume = false;
    _emit('update');
  }
}
function _onPaymentChange() {
  if (g_signupOrg) {
    const list = PaymentMethodStore.getList(g_signupOrg.org_id);
    const found = list?.find?.((p) => !p.is_deleted);
    g_hasPayment = Boolean(found);
  } else {
    g_hasPayment = false;
  }
  _emit('change');
}
function _onVolumeChange() {
  if (g_signupOrg?.org_id) {
    const list = VolumeStore.getVolumeList(g_signupOrg.org_id);
    g_hasVolume = list?.length > 0;
  } else {
    g_hasVolume = false;
  }
  _emit('change');
}
function _onPlanChange() {
  if (PlanStore.isReady() && g_planId) {
    if (!PlanStore.getPlan(g_planId)) {
      g_planId = '';
      _emit('change');
    }
  }
}
function _saveOrg(org_id) {
  window.localStorage[SIGNUP_ORG_KEY] = org_id;
}
function _loadOrg() {
  const org_id = parseInt(window.localStorage[SIGNUP_ORG_KEY]);
  const user = UserStore.getUser();
  if (org_id > 0 && user) {
    const found = user.org_list?.find?.((org) => org.org_id === org_id);
    if (found && found.is_admin) {
      g_signupOrg = found;
    }
  }
}
function _isReady() {
  return g_isReady;
}
function _getOrg() {
  return g_signupOrg;
}
function _getHasVolume() {
  return g_hasVolume;
}
function _getHasPayment() {
  return g_hasPayment;
}
function _getPlan(): any {
  let ret;
  const plan_id = g_signupOrg?.plan_id || g_planId;
  if (plan_id) {
    ret = PlanStore.getPlan(plan_id);
  }
  return ret;
}
export function useIsReady() {
  return React.useSyncExternalStore(_subscribe, _isReady);
}
export function useOrg() {
  return React.useSyncExternalStore(_subscribe, _getOrg);
}
export function useHasVolume() {
  return React.useSyncExternalStore(_subscribe, _getHasVolume);
}
export function useHasPayment() {
  return React.useSyncExternalStore(_subscribe, _getHasPayment);
}
export function usePlan() {
  return React.useSyncExternalStore(_subscribe, _getPlan);
}
export function createOrg(org_name: string, done) {
  let org_id;
  asyncSeries(
    [
      (done) => {
        OrgStore.createOrg({ org_name }, (_, new_org_id) => {
          org_id = new_org_id;
          done();
        });
      },
      (done) => {
        if (org_id) {
          done();
        } else {
          UserStore.verifySessionKey({}, () => {
            const user = UserStore.getUser();
            const found = user?.org_list?.[0];
            if (found?.plan_id === null) {
              org_id = found.org_id;
            }
            done();
          });
        }
      },
    ],
    (err) => {
      if (!err && org_id) {
        UserStore.setCurrentOrgId(org_id);
        g_signupOrg = UserStore.getOrg();
        _saveOrg(org_id);
        _emit('change');
      }
      done(err);
    }
  );
}
export function savePlan(plan: any) {
  g_planId = plan?.plan_id || null;
  _emit('update');
}
export function updatePlan(done) {
  const opts = { org_id: g_signupOrg.org_id, plan_id: g_planId };
  OrgStore.updatePlan(opts, (err) => {
    if (!err) {
      g_signupOrg = UserStore.getOrg();
      _emit('change');
    }
    done(err);
  });
}
export function finish(done) {
  _saveOrg(null);
  g_signupOrg = null;
  g_hasPayment = false;
  g_hasVolume = false;
  done?.();
}
